import axios from 'axios';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '../../../hooks';
import { TGameData, TParticipant, TParticiparteResponse } from '../../../types';

import { ajinomotoWheelId } from '..';
import { participate } from '../../../apiRoutes';
import Card from '../../common/Card';
import ClickHereCircle from './ClickHereCircle';
import DescriptionCard from './DescriptionCard';
import Footer from './Footer';
import LogoCard from './LogoCard';
import LoserCard from './LoserCard';
import WinnerCard from './WinnerCard';
import {
  StyledAsteriskText,
  StyledFortuneWheelContainer,
  StyledGameContainer,
  StyledGameContainerMobile,
  StyledGameName,
  StyledGrid,
  StyledPointer,
  StyledRightContainer,
  StyledWheel,
} from './style';

interface TFortuneWheelProps {
  game: TGameData;
  participant: TParticipant;
  userToken: string;
  imagesPath?: string;
  footerColor?: string;
  backgroundSize?: string;
  backgroundMobileSize?: string;
  hasMobileBackground?: boolean;
  backgroundRepeat?: boolean;
}

const FortuneWheel: React.FC<TFortuneWheelProps> = ({
  game,
  userToken,
  imagesPath = 'assets/classicWheel',
  footerColor,
  backgroundSize,
  backgroundMobileSize,
  hasMobileBackground,
  backgroundRepeat = true,
}: TFortuneWheelProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const [hasLaunchedWheel, setHasLaunchedWheel] = useState(false);
  const [preRequestSpinning, setPreRequestSpinning] = useState(false);
  const [postRequestSpinning, setPostRequestSpinning] = useState(false);
  const [finalRotationDegree, setFinalRotationDegree] = useState(0);
  const [gameResult, setGameResult] = useState<TParticiparteResponse['data'] | false>(false);

  const launchWheel = async () => {
    setHasLaunchedWheel(true);
    if (gameResult || hasLaunchedWheel) return;

    const rotationDegree = 360 * 8;
    setPreRequestSpinning(true);

    const { data }: TParticiparteResponse = await axios.get(participate(userToken));

    setPreRequestSpinning(false);
    setPostRequestSpinning(true);

    if (data.winner) {
      setFinalRotationDegree(rotationDegree + 240);
      setTimeout(() => {
        setGameResult(data);
      }, 2500);
    } else {
      setFinalRotationDegree(rotationDegree + 268);
      setTimeout(() => {
        setGameResult(data);
      }, 2500);
    }
  };

  return (
    <Fragment>
      {isMobile ? (
        <Fragment>
          <StyledGameContainerMobile
            $imgPath={imagesPath}
            $hasMobileBackground={hasMobileBackground}
            $backgroundMobileSize={backgroundMobileSize}
          >
            <StyledGameName src={require(`../../../${imagesPath}/gameNameMobile.png`)} $isMobile />
            <StyledFortuneWheelContainer $isMobile>
              <ClickHereCircle gameAnimationType={game.animationType} />
              <StyledPointer src={require(`../../../${imagesPath}/pointeur.png`)} />
              <StyledWheel
                $preRequestSpinning={preRequestSpinning}
                $postRequestSpinning={postRequestSpinning}
                $finalRotationDegree={finalRotationDegree}
                onClick={launchWheel}
                $hasPlayed={!!gameResult}
                $isMobile
                $imgPath={imagesPath}
              />
            </StyledFortuneWheelContainer>
          </StyledGameContainerMobile>
          <Fragment>
            {gameResult ? (
              <Card
                maxWidth="500px"
                width="unset"
                padding="20px 24px"
                noShadows
                marginBottom="20px"
                borderRadius="0"
                margin="-50px 0px 30px 0px"
              >
                {gameResult && gameResult?.winner && (
                  <WinnerCard gameResult={gameResult} winningText={game.winningText} />
                )}
                {gameResult && !gameResult?.winner && <LoserCard gameResult={gameResult} />}
              </Card>
            ) : (
              <Fragment>
                <Card
                  maxWidth="500px"
                  width="auto"
                  padding="20px 24px"
                  noShadows
                  marginBottom="20px"
                  borderRadius="0"
                  margin="-30px 0px 30px 0px"
                >
                  <LogoCard logoSrc={game.logo} gameAnimationType={game.animationType} />
                  <DescriptionCard game={game} />
                </Card>
              </Fragment>
            )}
          </Fragment>
          <Footer game={game} />
        </Fragment>
      ) : (
        <Fragment>
          <StyledGameContainer
            $imgPath={imagesPath}
            $backgroundSize={backgroundSize}
            $backgroundRepeat={backgroundRepeat}
          >
            <StyledGrid>
              <StyledRightContainer>
                <StyledGameName src={require(`../../../${imagesPath}/gameName.png`)} />
                <Card
                  maxWidth="402px"
                  width=""
                  padding="100px 24px"
                  noShadows={isMobile}
                  marginBottom="20px"
                  borderRadius="0"
                >
                  <LogoCard logoSrc={game.logo} gameAnimationType={game.animationType} />
                  {!gameResult && <DescriptionCard game={game} />}
                  {gameResult && gameResult?.winner && (
                    <WinnerCard gameResult={gameResult} winningText={game.winningText} />
                  )}
                  {gameResult && !gameResult?.winner && <LoserCard gameResult={gameResult} />}
                </Card>
              </StyledRightContainer>
              <StyledFortuneWheelContainer>
                <ClickHereCircle gameAnimationType={game.animationType} />
                <StyledPointer src={require(`../../../${imagesPath}/pointeur.png`)} />
                <StyledWheel
                  $preRequestSpinning={preRequestSpinning}
                  $postRequestSpinning={postRequestSpinning}
                  $finalRotationDegree={finalRotationDegree}
                  onClick={launchWheel}
                  $hasPlayed={!!gameResult}
                  $imgPath={imagesPath}
                />
              </StyledFortuneWheelContainer>
            </StyledGrid>
            {game?.animationType === ajinomotoWheelId && (
              <StyledAsteriskText>{t('ajinomotoAsterisk')}</StyledAsteriskText>
            )}
            <Footer game={game} footerColor={footerColor} />
          </StyledGameContainer>
        </Fragment>
      )}
    </Fragment>
  );
};

export default FortuneWheel;
